import { createSelector } from 'reselect';
import { Map } from 'immutable';
import { KITCHEN_ISLAND_ID } from 'domain/Options/generators/kitchenIslands';
import { WALL_PANEL_ID, WALL_EDGING_STRIP_CUSTOMIZABLE_ID } from 'domain/Options/generators/splashbacks';
import {
  expressionParameterSelector,
  kitchenIslandExpressionParameterSelector,
  selectedThicknessSelector,
  selectedKitchenIslandThicknessSelector,
  wallPanelExpressionParameterSelector,
  splashbackParameterSelector,
  wallEdgingStripCustomizableParameterSelector,
  kitchenIslandParameterSelector,
  wallPanelMaterialParameterSelector,
  materialParameterSelector
} from './parameters';
import { optionsSelector } from '../selectors';
import { types as shapeTypes } from '../generators/shapes';

const measurementsSelector = createSelector(
  optionsSelector,
  (options) => {
    return options.get('measurements');
  }
);
const wallEdgingStripCustomizableMeasurementsSelector = createSelector(
  optionsSelector,
  (options) => {
    return options.get('wallEdgingStripCustomizableMeasurements');
  }
);

export const wallEdgingStripCustomizableMinHeightSelector = createSelector(
  wallEdgingStripCustomizableMeasurementsSelector,
  (wallEdgingStripCustomizableMeasurements) => {
    try {
      return wallEdgingStripCustomizableMeasurements.get('minHeight');
    } catch (err) {
      return 500;
    }
  });
  
export const wallEdgingStripCustomizableMaxHeightSelector = createSelector(
  wallEdgingStripCustomizableMeasurementsSelector,
  (wallEdgingStripCustomizableMeasurements) => {
    try {
      return wallEdgingStripCustomizableMeasurements.get('maxHeight');
    } catch (err) {
      return 500;
    }
  });

const wallPanelMeasurementsSelector = createSelector(
  optionsSelector,
  (options) => {
    return options.get('wallPanelMeasurements');
  }
);

export const wallPanelMinHeightSelector = createSelector(
  wallPanelMeasurementsSelector, 
  wallPanelMaterialParameterSelector,
  (wallPanelMeasurements, wallPanelMaterial) => {
    const wallPanelHeights = wallPanelMaterial && wallPanelMaterial.getIn(['value', 'expressions', 0, 'heights', 0]);
    const minHeight = wallPanelHeights && wallPanelHeights.get('minHeight');
    try {
      if(minHeight !== undefined && !isNaN(minHeight)) {
        return minHeight;
      }
      if(isNaN(minHeight) && wallPanelMaterial.getIn(['value', 'id']) === 'laminate' && wallPanelMeasurements.get('minHeightLaminate')){
        return wallPanelMeasurements.get('minHeightLaminate');
      }
      return wallPanelMeasurements.get('minHeight');
    } catch (err) {
      return 500;
    }
  });

export const wallPanelMaxHeightSelector = createSelector(
  wallPanelMeasurementsSelector,
  wallPanelExpressionParameterSelector,
  splashbackParameterSelector,
  (measurements, expressionParameter, splashbackParameter) => {
    if (splashbackParameter.getIn(['value', 'id']) !== WALL_PANEL_ID) return NaN;

    const expression = expressionParameter.get('value');

    if (expression) {
      if (expression.get('priceByArea')) {
        return measurements.get('maxHeight');
      }

      return expression.get('heights').sort((a, b) => b.get('maxHeight') - a.get('maxHeight')).getIn([0, 'maxHeight']);
    } else {
      return measurements.get('maxHeight');
    }
  }
);

export const wallPanelMaxLengthSelector = createSelector(
  wallPanelMeasurementsSelector,
  (wallPanelMeasurements) => wallPanelMeasurements.get('maxLength')
);


export const maxDepthSelector = createSelector(
  expressionParameterSelector,
  measurementsSelector,
  selectedThicknessSelector,
  (expressionParameter, measurements, selectedThickness) => {
    if (expressionParameter.getIn(['value', 'id'])) {
      if (selectedThickness !== null && selectedThickness !== undefined) {
        if (selectedThickness.get('priceByArea')) {
          const maxDepthDecentralized = selectedThickness.getIn(['depths', '0']).get('maxDepth');
          if (maxDepthDecentralized !== null && maxDepthDecentralized !== undefined && !isNaN(maxDepthDecentralized)) {
            return maxDepthDecentralized;
          }
          if (measurements.get('maxDepthDecentralized')) {
            console.error("Max. depth from de-centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, fallback maxDepthDecentralized used from business rules, value: ", measurements.get('maxDepthDecentralized'));
            return measurements.get('maxDepthDecentralized');
          } else {
            console.error("Max. depth from de-centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, fallback default maxDepthDecentralized from business rules not found, returning: ", null);
            return null;
          }
        } else {
          const maxDepthCentralized = selectedThickness.get('depths').sort((a, b) => b.get('maxDepth') - a.get('maxDepth')).getIn([0, 'maxDepth']);
          if (maxDepthCentralized !== null && maxDepthCentralized !== undefined && !isNaN(maxDepthCentralized)) {
            return maxDepthCentralized;
          }
          if (measurements.get('maxDepthCentralized')) {
            console.error("Max. depth from centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, default value used from business rules, value: ", measurements.get('maxDepthCentralized'));
            return measurements.get('maxDepthCentralized');
          } else {
            console.error("Max. depth from de-centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, fallback default maxDepthDecentralized from business rules not found, returning: ", null);
            return null;
          }
        }
      }
      return measurements.get('maxDepthCentralized');
    } else {
      console.error("maxDepthSelector - selected expression not found");
      return measurements.get('maxDepthCentralized');
    }
  }
);

export const kitchenIslandMinLengthSelector = createSelector(
  measurementsSelector,
  selectedKitchenIslandThicknessSelector,
  kitchenIslandParameterSelector,
  (measurements, selectedThickness, kitchenIsland) => {
    const minLength = selectedThickness && selectedThickness.getIn(['depths', '0']).get('minLength');
    if(kitchenIsland.getIn(['value', 'id']) === KITCHEN_ISLAND_ID && minLength !== undefined && !isNaN(minLength)) {
      return minLength;
    }
    return measurements.get('minLength');
  }
);

export const kitchenIslandMinDepthSelector = createSelector(
  measurementsSelector,
  selectedKitchenIslandThicknessSelector,
  kitchenIslandParameterSelector,
  (measurements, selectedThickness, kitchenIsland) => {    
    const minDepth = selectedThickness && selectedThickness.getIn(['depths', '0']).get('minDepth');
    if(kitchenIsland.getIn(['value', 'id']) === KITCHEN_ISLAND_ID && minDepth !== undefined && !isNaN(minDepth)) {
      return minDepth;
    }
    return measurements.get('minDepth');
  }
);

export const kitchenIslandMaxDepthSelector = createSelector(
  kitchenIslandExpressionParameterSelector,
  measurementsSelector,
  selectedKitchenIslandThicknessSelector,
  (expressionParameter, measurements, selectedThickness) => {
    if (expressionParameter.getIn(['value', 'id'])) {
      if (selectedThickness !== null && selectedThickness !== undefined) {
        if (selectedThickness.get('priceByArea')) {
          const maxDepthDecentralized = selectedThickness.getIn(['depths', '0']).get('maxDepth');
          if (maxDepthDecentralized !== null && maxDepthDecentralized !== undefined && !isNaN(maxDepthDecentralized)) {
            return maxDepthDecentralized;
          }
          if (measurements.get('maxDepthDecentralized')) {
            console.error("Max. depth from de-centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, fallback maxDepthDecentralized used from business rules, value: ", measurements.get('maxDepthDecentralized'));
            return measurements.get('maxDepthDecentralized');
          } else {
            console.error("Max. depth from de-centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, fallback default maxDepthDecentralized from business rules not found, returning: ", null);
            return null;
          }
        } else {
          const maxDepthCentralized = selectedThickness.get('depths').sort((a, b) => b.get('maxDepth') - a.get('maxDepth')).getIn([0, 'maxDepth']);
          if (maxDepthCentralized !== null && maxDepthCentralized !== undefined && !isNaN(maxDepthCentralized)) {
            return maxDepthCentralized;
          }
          if (measurements.get('maxDepthCentralized')) {
            console.error("Max. depth from centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, default value used from business rules, value: ", measurements.get('maxDepthCentralized'));
            return measurements.get('maxDepthCentralized');
          } else {
            console.error("Max. depth from de-centralized expression: ", expressionParameter.getIn(['value', 'id']), " not found/incorrect, fallback default maxDepthDecentralized from business rules not found, returning: ", null);
            return null;
          }
        }
      }
      return measurements.get('maxDepthCentralized');
    } else {
      console.error("maxDepthSelector - selected expression not found");
      return measurements.get('maxDepthCentralized');
    }
  }
);

export const minDepthSelector = createSelector(
  measurementsSelector,
  selectedThicknessSelector,
  materialParameterSelector,
  wallPanelMaterialParameterSelector,
  splashbackParameterSelector,
  (measurements, selectedThickness, materialParameter, wallPanelMaterial, splashbackParameter) => {
    const selectedMaterial = materialParameter.getIn(['value', 'id']);
    const minDepth = selectedThickness && selectedThickness.getIn(['depths', '0']).get('minDepth');
    const selectedSplashback = splashbackParameter.getIn(['value', 'id']);
    const wallPanelMeasurements = wallPanelMaterial && wallPanelMaterial.getIn(['value', 'expressions', 0, 'heights', 0]);
    const minWallPanelHeight = wallPanelMeasurements && wallPanelMeasurements.get('minHeight');
    if(selectedMaterial !== 'noMaterial' && minDepth !== undefined && !isNaN(minDepth)) {
      return minDepth;
    }
    if(selectedMaterial === 'noMaterial' && selectedSplashback === WALL_PANEL_ID && minWallPanelHeight !== undefined && !isNaN(minWallPanelHeight)) {
      return minWallPanelHeight;
    }
    return measurements.get('minDepth');
  }
);

export const minLengthSelector = createSelector(
  measurementsSelector,
  materialParameterSelector,
  selectedThicknessSelector,
  wallPanelMaterialParameterSelector,
  splashbackParameterSelector,
  (measurements, materialParameter, selectedThickness, wallPanelMaterial, splashbackParameter) => {
    const selectedMaterial = materialParameter.getIn(['value', 'id']);
    const minLength = selectedThickness && selectedThickness.getIn(['depths', '0']).get('minLength');
    const selectedSplashback = splashbackParameter.getIn(['value', 'id']);
    const wallPanelMeasurements = wallPanelMaterial && wallPanelMaterial.getIn(['value', 'expressions', 0, 'heights', 0]);
    const minWallPanelLength = wallPanelMeasurements && wallPanelMeasurements.get('minLength');
    if(selectedMaterial !== 'noMaterial' && minLength !== undefined && !isNaN(minLength)) {
      return minLength;
    }
    if(selectedMaterial === 'noMaterial' && selectedSplashback === WALL_PANEL_ID && minWallPanelLength !== undefined && !isNaN(minWallPanelLength)) {
      return minWallPanelLength;
    }
    return measurements.get('minLength');
  }
);

export const maxLengthSelector = createSelector(
  measurementsSelector,
  (measurements) => {
    return measurements.get('maxLength');
  }
);

export const irregularDiffSelector = createSelector(
  measurementsSelector,
  (measurements) => {
    return measurements.get('irregular');
  }
);

export const kitchenIslandMeasurementsLimitSelector = createSelector(
  kitchenIslandMinDepthSelector,
  kitchenIslandMaxDepthSelector,
  kitchenIslandMinLengthSelector,
  maxLengthSelector,
  irregularDiffSelector,
  (minDepth, maxDepth, minLength, maxLength, irregular) => ({
    [shapeTypes.DEPTH]: { min: minDepth, max: maxDepth },
    [shapeTypes.LENGTH]: { min: minLength, max: maxLength },
    irregular,
  }),
);

export const measurementsLimitSelector = createSelector(
  minDepthSelector,
  maxDepthSelector,
  minLengthSelector,
  maxLengthSelector,
  irregularDiffSelector,
  (minDepth, maxDepth, minLength, maxLength, irregular) => ({
    [shapeTypes.DEPTH]: { min: minDepth, max: maxDepth },
    [shapeTypes.LENGTH]: { min: minLength, max: maxLength },
    irregular,
  }),
);

export const wallPanelHeightSelector = createSelector(
  splashbackParameterSelector,
  (splashbackParameter) => {
    if (splashbackParameter.getIn(['value', 'id']) !== WALL_PANEL_ID) {
      return undefined;
    }
    const height = splashbackParameter.get('height');
    return (height || height === 0) ? height : splashbackParameter.getIn(['value', 'defaultHeight']);
  }
);

export const wallEdgingStripHeightSelector = createSelector(
  splashbackParameterSelector,
  wallEdgingStripCustomizableParameterSelector,
  (splashbackParameter, wallEdgingStripCustomizableParameter) => {
    if (splashbackParameter.getIn(['value', 'id']) !== WALL_EDGING_STRIP_CUSTOMIZABLE_ID) {
      return undefined;
    }
    const height = wallEdgingStripCustomizableParameter.get('height');
    return (height || height === 0) ? height : splashbackParameter.getIn(['value', 'defaultHeight']);
  }
);

export const kitchenIslandMeasurementsSelector = createSelector(
  kitchenIslandParameterSelector,
  (kitchenIsland) => {
    if (kitchenIsland.getIn(['value', 'id']) !== KITCHEN_ISLAND_ID) {
      return undefined;
    }
    return Map(kitchenIsland.getIn(['value', 'dimensions']).reduce((measurements, dimension, dimensionName) => {
      return {
        ...measurements,
        [dimensionName]: kitchenIsland.getIn(['measurements', dimensionName]) || dimension.get('defaultValue'),
      };
    }, {}));
  }
);
